import { useAtom } from 'jotai';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { ContactsTagsAndGroups } from '@/src/ContactsModule/components/ContactTagsAndGroups/ContactsTagsAndGroups';
import { useMutationVerifyState } from '@/src/ContactsModule/contexts/ImportFileContext/ImportFileContext';
import { IEmblueService } from '@/src/infrastructure/Protocol/IEmblueService';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { ImportSteps } from '@/src/modules/ContactsModule/screens/ImportMain/ImportStepEnum.enum';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';

import { atomSelectedGroups, atomStep4CreateGroup } from '@/modules/ContactsModule/atoms/Step4';
import { atomFiles } from '@/modules/ContactsModule/atoms/Steps';

interface IPropsStep4 {
  cancelProcess: () => void;
}

const Step4 = (props: IPropsStep4) => {
  const { cancelProcess } = props;
  const [selectedGroups, setSelectedGroups] = useAtom(atomSelectedGroups);
  const [files] = useAtom(atomFiles);
  const { t } = useTranslation();
  const { verifyProcessedImport } = useMutationVerifyState();
  const [isCreateGroup] = useAtom(atomStep4CreateGroup);
  const [showDisableGroupError, setShowDisableGroupError] = useState(false);

  const service = useService() as IEmblueService;

  const saveImport = useCallback(async () => {
    if (isCreateGroup && selectedGroups.length === 0) {
      setShowDisableGroupError(true);
      return;
    }

    if (files?.state === loadingStatus.ready && files.groups?.selectedGroupsId && files?.process) {
      await service?.import({
        importData: {
          groups: files.groups?.selectedGroupsId,
          idProcess: files?.process,
        },
      });
      verifyProcessedImport();
    }
  }, [
    files.groups?.selectedGroupsId,
    files?.process,
    files?.state,
    isCreateGroup,
    selectedGroups.length,
    service,
    verifyProcessedImport,
  ]);

  useEffect(() => {
    if (showDisableGroupError && selectedGroups.length > 0) setShowDisableGroupError(false);

    if (!isCreateGroup && showDisableGroupError) setShowDisableGroupError(false);
  }, [isCreateGroup, selectedGroups, showDisableGroupError]);

  if (files?.step !== ImportSteps.tagsAndGroup) return null;

  return (
    <>
      <Flex column>
        <div className="w-full px-10 py-20">
          <ContactsTagsAndGroups
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
            showGroupError={showDisableGroupError}
          />
        </div>
        <Flex
          withGap
          alignment="end"
          className="border-emblue-border w-full border-t px-10 py-2"
          gapSize="small"
        >
          <Button
            outline
            secondary
            disabled={files.showStateCard}
            id="import-cancel"
            onClick={cancelProcess}
          >
            {t('IMPORT_MAIN.cancel')}
          </Button>
          <Button
            disabled={files.showStateCard || showDisableGroupError}
            id="import-continue"
            onClick={() => void saveImport()}
          >
            {t('IMPORT_MAIN.confirmImportation')}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default memo(Step4);
